import { Button, tokens } from "shared/components/Spor";
import { FC, MutableRefObject, useContext } from "react";
import { PlanMeasureKey } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/types";
import {
  ArrowDownOutline18Icon,
  ArrowUpOutline18Icon,
  TracksOutline24Icon,
  TrainOutline24Icon,
} from "@vygruppen/spor-icon-react";
import { useObserveIntersection } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumn/useObserveIntersection";
import styled, { css } from "styled-components";
import {
  CountGroup,
  MeasureOverviewColumnSeverityContext,
} from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverviewColumnSeverityContext";

const CommonScrollButtonContainer = css`
  display: flex;
  flex-flow: column nowrap;
  gap: ${tokens.default.size.spacing[1.5]};
  align-items: center;
`;

export const TopScrollButtonsContainer = styled.div`
  ${CommonScrollButtonContainer}
`;
export const BottomScrollButtonsContainer = styled.div`
  ${CommonScrollButtonContainer}
`;

function labelForCountGroup({ alarm, warning }: CountGroup): string {
  const alarmCount = `${alarm} ${alarm > 1 ? "nye hendelser" : "ny hendelse"}`;
  const warningCount = `${warning} ${warning > 1 ? "endringer" : "endring"}`;

  return `${alarm ? alarmCount : ""}${alarm && warning ? " og " : ""}${warning ? warningCount : ""}`;
}

type ScrollButtonsProps = {
  measureKey: PlanMeasureKey;
  infraScrollRef: MutableRefObject<HTMLDivElement | null>;
  trainScrollRef: MutableRefObject<HTMLDivElement | null>;
  rootRef: MutableRefObject<HTMLElement | null>;
};

export const ScrollButtons: FC<ScrollButtonsProps> = ({
  measureKey,
  infraScrollRef,
  trainScrollRef,
  rootRef,
}) => {
  const { countState, severityState } = useContext(
    MeasureOverviewColumnSeverityContext,
  );

  const infraCounts = countState[measureKey].infrastructure;
  const trainCounts = countState[measureKey].train;

  const { isIntersecting: infraVisible } = useObserveIntersection(
    infraScrollRef,
    rootRef,
  );
  const { intersectionState: trainIntersectionState } = useObserveIntersection(
    trainScrollRef,
    rootRef,
  );

  const infraSemantics =
    severityState[measureKey].infrastructure === "transparent"
      ? null
      : severityState[measureKey].infrastructure;

  const trainSemantics =
    severityState[measureKey].train === "transparent"
      ? null
      : severityState[measureKey].train;

  return (
    <>
      <TopScrollButtonsContainer>
        {infraSemantics && !infraVisible && (
          <Button
            size="sm"
            width="max-content"
            semantic={infraSemantics}
            onClick={() =>
              infraScrollRef.current?.scrollIntoView({ behavior: "smooth" })
            }
            leftIcon={<TracksOutline24Icon />}
            rightIcon={<ArrowUpOutline18Icon />}
          >
            {labelForCountGroup(infraCounts)}
          </Button>
        )}

        {trainSemantics && trainIntersectionState === "above" && (
          <Button
            size="sm"
            width="max-content"
            semantic={trainSemantics}
            onClick={() =>
              trainScrollRef.current?.scrollIntoView({ behavior: "smooth" })
            }
            leftIcon={<TrainOutline24Icon />}
            rightIcon={<ArrowUpOutline18Icon />}
          >
            {labelForCountGroup(trainCounts)}
          </Button>
        )}
      </TopScrollButtonsContainer>

      <BottomScrollButtonsContainer>
        {trainSemantics && trainIntersectionState === "below" && (
          <Button
            size="sm"
            width="max-content"
            semantic={trainSemantics}
            onClick={() =>
              trainScrollRef.current?.scrollIntoView({ behavior: "smooth" })
            }
            leftIcon={<TrainOutline24Icon />}
            rightIcon={<ArrowDownOutline18Icon />}
          >
            {labelForCountGroup(trainCounts)}
          </Button>
        )}
      </BottomScrollButtonsContainer>
    </>
  );
};
